export type ApiErrorPayload = {
  code?: string;
  message?: string;
  retryable?: boolean;
  detail?: Record<string, unknown>;
};

export class ApiError extends Error {
  code: string;
  retryable: boolean;
  status: number;

  constructor(status: number, payload?: ApiErrorPayload) {
    super(payload?.message || "服务暂时不可用，请稍后重试。");
    this.name = "ApiError";
    this.code = payload?.code || "REQUEST_FAILED";
    this.retryable = Boolean(payload?.retryable);
    this.status = status;
  }
}

export type Health = {
  status: string;
  version: string;
  ffmpeg_available: boolean;
  asr_provider: string;
  asr_ready: boolean;
  target_seconds: number;
};

export type ErrorPayload = Required<Pick<ApiErrorPayload, "code" | "message">> &
  Pick<ApiErrorPayload, "retryable" | "detail">;

export type ProfileAudit = {
  input_url: string;
  profile_url?: string;
  sec_uid?: string;
  nickname?: string;
  status: "succeeded" | "failed";
  pages_captured: number;
  collected_total: number;
  included_total: number;
  earliest_published_at?: string | null;
  latest_published_at?: string | null;
  stop_reason?: string;
  error?: ErrorPayload | null;
};

export type KnowledgeCounts = {
  requested_profiles: number;
  resolved_profiles: number;
  collected_works: number;
  video_works: number;
  skipped_non_video: number;
  batches: number;
  transcription_attempts: number;
  transcribed: number;
  failed: number;
  markdown_files: number;
};

export type KnowledgeFailure = {
  aweme_id?: string;
  canonical_url?: string;
  author_name?: string;
  title?: string;
  published_at?: string;
  attempts?: number;
  code: string;
  message: string;
  retryable?: boolean;
};

export type KnowledgeResult = {
  knowledge_base_name: string;
  result_path: string;
  manifest_path: string;
  archive_path: string;
  manifest_url: string;
  archive_url: string;
  counts: KnowledgeCounts;
  coverage_verified: boolean;
  coverage_warnings: string[];
  audits: ProfileAudit[];
  failures: KnowledgeFailure[];
};

export type KnowledgeBuild = {
  id: string;
  status:
    | "queued"
    | "collecting"
    | "waiting_user"
    | "transcribing"
    | "building"
    | "packaging"
    | "succeeded"
    | "partial"
    | "failed"
    | "cancelled";
  progress: number;
  message: string;
  created_at: string;
  updated_at: string;
  elapsed_ms: number;
  request: {
    share_texts: string[];
    start_date: string;
    knowledge_base_name: string;
    include_gallery: boolean;
    retry_attempts: number;
  };
  counts: KnowledgeCounts;
  retry_of?: string | null;
  result?: KnowledgeResult | null;
  error?: ErrorPayload | null;
};

export type CreateKnowledgeRequest = KnowledgeBuild["request"];

export type TranscriptStage = {
  name: string;
  label: string;
  status: "pending" | "running" | "succeeded" | "skipped" | "failed";
  elapsed_ms?: number | null;
};

export type TranscriptJob = {
  id: string;
  status: string;
  progress: number;
  message: string;
  elapsed_ms: number;
  target_ms: number;
  stages: TranscriptStage[];
  result?: {
    video: {
      item_id: string;
      canonical_url: string;
      title: string;
      author_name: string;
      duration_seconds?: number | null;
      cover_url?: string | null;
      published_text?: string;
    };
    transcript: {
      source: string;
      provider: string;
      raw_transcript: string;
      clean_copy: string;
      srt: string;
    };
    cache_hit: boolean;
    completed_in_ms: number;
    within_10s_target: boolean;
  } | null;
  error?: ErrorPayload | null;
};

export type TranscriptBatch = {
  id: string;
  status: "queued" | "running" | "succeeded" | "partial" | "failed";
  progress: number;
  message: string;
  created_at: string;
  updated_at: string;
  total: number;
  queued: number;
  running: number;
  succeeded: number;
  failed: number;
  items: Array<{ index: number; share_text: string; job: TranscriptJob }>;
};

export type ProfileWork = {
  aweme_id: string;
  canonical_url: string;
  author_name: string;
  profile_url: string;
  title: string;
  published_at: string;
  content_type: "video" | "gallery";
  is_pinned: boolean;
};

export type ProfileCollection = {
  id: string;
  status:
    | "queued"
    | "resolving"
    | "launching_browser"
    | "collecting"
    | "waiting_user"
    | "succeeded"
    | "partial"
    | "failed"
    | "cancelled";
  progress: number;
  message: string;
  elapsed_ms: number;
  result?: {
    start_date: string;
    include_gallery: boolean;
    requested_profiles: number;
    resolved_profiles: number;
    succeeded_profiles: number;
    failed_profiles: number;
    duplicates_removed: number;
    collected_total: number;
    included_total: number;
    works: ProfileWork[];
    audits: ProfileAudit[];
  } | null;
  error?: ErrorPayload | null;
};

export type ContentItem = {
  aweme_id: string;
  title: string;
  author_name: string;
  published_at: string;
  canonical_url: string;
  profile_url: string;
  content_type: "video" | "gallery";
  is_pinned: boolean;
  transcription_status: "queued" | "processing" | "succeeded" | "failed" | "unavailable";
  review_status: string;
  topics: string[];
  failure?: ErrorPayload | null;
};

export type ContentDetail = ContentItem & {
  raw_transcript: string;
  clean_copy: string;
  transcript_source: string;
  transcript_provider: string;
  note_available: boolean;
};

type CreateResponse<T> = { job: T; poll_url: string };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json") ? await response.json() : undefined;
  if (!response.ok) throw new ApiError(response.status, body?.error);
  return body as T;
}

export const api = {
  health: () => request<Health>("/healthz"),

  createKnowledge: (payload: CreateKnowledgeRequest) =>
    request<CreateResponse<KnowledgeBuild>>("/api/v1/research-projects", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getKnowledge: (id: string) => request<KnowledgeBuild>(`/api/v1/research-projects/${id}`),
  cancelKnowledge: (id: string) =>
    request<KnowledgeBuild>(`/api/v1/research-projects/${id}`, { method: "DELETE" }),
  retryKnowledge: (id: string) =>
    request<CreateResponse<KnowledgeBuild>>(`/api/v1/research-projects/${id}/retry`, {
      method: "POST",
    }),
  getManifest: (url: string) => request<KnowledgeManifest>(url),
  async listKnowledge(): Promise<KnowledgeBuild[] | null> {
    try {
      return await request<KnowledgeBuild[]>("/api/v1/research-projects");
    } catch (error) {
      if (error instanceof ApiError && [404, 405].includes(error.status)) return null;
      throw error;
    }
  },
  async listContents(id: string): Promise<ContentItem[] | null> {
    try {
      const payload = await request<{ project_id: string; total: number; items: ContentItem[] }>(
        `/api/v1/research-projects/${id}/contents`,
      );
      return payload.items;
    } catch (error) {
      if (error instanceof ApiError && [404, 405].includes(error.status)) return null;
      throw error;
    }
  },
  getContent: (projectId: string, awemeId: string) =>
    request<ContentDetail>(`/api/v1/research-projects/${projectId}/contents/${awemeId}`),
  projectArtifactUrl: (projectId: string, artifact: "links" | "manifest" | "archive") => {
    const path = artifact === "links" ? "links.csv" : artifact === "manifest" ? "manifest.json" : "archive.zip";
    return `/api/v1/research-projects/${projectId}/${path}`;
  },
  contentArtifactUrl: (projectId: string, awemeId: string, artifact: "transcript" | "subtitles") =>
    `/api/v1/research-projects/${projectId}/contents/${awemeId}/${artifact === "transcript" ? "transcript.txt" : "subtitles.srt"}`,
  async hasSubtitles(projectId: string, awemeId: string) {
    const response = await fetch(
      `/api/v1/research-projects/${projectId}/contents/${awemeId}/subtitles.srt`,
      { cache: "no-store" },
    );
    return response.ok;
  },

  createTranscriptBatch: (share_texts: string[]) =>
    request<{ batch: TranscriptBatch; poll_url: string; duplicates_removed: number }>(
      "/api/v1/transcriptions/batches",
      { method: "POST", body: JSON.stringify({ share_texts, wait_ms: 0 }) },
    ),
  getTranscriptBatch: (id: string) =>
    request<TranscriptBatch>(`/api/v1/transcriptions/batches/${id}`),
  cancelTranscriptBatch: (id: string) =>
    request<TranscriptBatch>(`/api/v1/transcriptions/batches/${id}`, { method: "DELETE" }),

  createProfileCollection: (payload: {
    share_texts: string[];
    start_date: string;
    include_gallery: boolean;
  }) =>
    request<CreateResponse<ProfileCollection>>("/api/v1/profile-collections", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  getProfileCollection: (id: string) =>
    request<ProfileCollection>(`/api/v1/profile-collections/${id}`),
  cancelProfileCollection: (id: string) =>
    request<ProfileCollection>(`/api/v1/profile-collections/${id}`, { method: "DELETE" }),
};

export type KnowledgeManifest = {
  job_id: string;
  knowledge_base_name: string;
  created_at: string;
  completed_at: string;
  start_date: string;
  counts: KnowledgeCounts;
  coverage_verified: boolean;
  coverage_warnings: string[];
  notes: string[];
};
